// src/Login.js
import React, { useState, useEffect } from 'react';
import { auth, db } from '../firebase';
import {
  signInWithEmailAndPassword,
  GoogleAuthProvider,
  signInWithPopup,
  onAuthStateChanged,
  sendEmailVerification,
} from 'firebase/auth';
import { useNavigate, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGoogle } from '@fortawesome/free-brands-svg-icons';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { Circles } from 'react-loader-spinner';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(true); // Loading state
  const [verificationError, setVerificationError] = useState('');
  const [currentUser, setCurrentUser] = useState(null);
  const [verificationSuccess, setVerificationSuccess] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Check if user just verified their email
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get('verified') === 'true') {
      setVerificationSuccess(true);
    }

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setCurrentUser(user);
        
        // If user is already logged in and verified, redirect to dashboard
        if (user.emailVerified) {
          const infoComplete = await checkUserInfo(user);
          if (infoComplete) {
            navigate('/dashboard');
          } else {
            navigate('/complete-info');
          }
        } else {
          // User is logged in but not verified
          setEmail(user.email);
          setVerificationError('Please verify your email before accessing the dashboard.');
          // Optionally, send a new verification email
          await sendEmailVerification(user, {
            url: window.location.origin + '/login?verified=true'
          });
          setLoading(false);
        }
      } else {
        setCurrentUser(null);
        setLoading(false); // Show login form when no user is authenticated
      }
    });

    return () => unsubscribe();
  }, [navigate, location]);

  const checkUserInfo = async (user) => {
    const docRef = doc(db, 'users', user.uid);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      const data = docSnap.data();
      return (
        data.firstName?.trim() !== '' &&
        data.lastName?.trim() !== '' &&
        data.role?.trim() !== ''
      );
    } else {
      return false;
    }
  };

  const createUserDocumentIfNotExists = async (user) => {
    const docRef = doc(db, 'users', user.uid);
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) {
      const displayName = user.displayName || '';
      const nameParts = displayName.split(' ');
      const firstNameGoogle = nameParts[0] || '';
      const lastNameGoogle = nameParts.slice(1).join(' ') || '';

      await setDoc(docRef, {
        firstName: firstNameGoogle.trim(),
        lastName: lastNameGoogle.trim(),
        email: user.email || '',
        role: '',
        institution: '',
        registrationTimestamp: new Date(),
        trialCredits: 30,
        emailVerified: user.emailVerified,
      });
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setVerificationError('');
    
    try {
      const userCredential = await signInWithEmailAndPassword(
        auth,
        email,
        password
      );
      const user = userCredential.user;
      
      // Check if email is verified
      if (!user.emailVerified) {
        setVerificationError('Please verify your email before logging in.');
        // Optionally, send a new verification email
        await sendEmailVerification(user, {
          url: window.location.origin + '/login?verified=true'
        });
        return;
      }

      const infoComplete = await checkUserInfo(user);
      if (infoComplete) {
        navigate('/dashboard');
      } else {
        navigate('/complete-info');
      }
    } catch (error) {
      console.error('Error during login:', error.message);
      alert(error.message);
    }
  };

  const handleGoogleLogin = async () => {
    const provider = new GoogleAuthProvider();
    try {
      const result = await signInWithPopup(auth, provider);
      const user = result.user;

      await createUserDocumentIfNotExists(user);

      const infoComplete = await checkUserInfo(user);
      if (infoComplete) {
        navigate('/dashboard');
      } else {
        navigate('/complete-info');
      }
    } catch (error) {
      console.error('Error during Google login:', error.message);
      alert(error.message);
    }
  };

  const handleResendVerification = async () => {
    if (currentUser) {
      try {
        await sendEmailVerification(currentUser, {
          url: window.location.origin + '/login?verified=true'
        });
        alert('Verification email has been resent. Please check your inbox.');
      } catch (error) {
        console.error('Error sending verification email:', error.message);
        alert(error.message);
      }
    }
  };

  if (loading) {
    return (
      <div className="loading-overlay">
        <div className="loading-content">
          <Circles height="80" width="80" color="#ffffff" ariaLabel="loading" />
          <p>Loading... Updating Login Info</p>
        </div>
      </div>
    );
  }

  return (
    <div className="login-container">
      <h2>Login</h2>
      {verificationSuccess ? (
        <div className="verification-success">
          <h3>Email Verified Successfully!</h3>
          <p>Your email has been verified. You can now log in to your account.</p>
        </div>
      ) : currentUser && !currentUser.emailVerified ? (
        <div className="verification-message">
          <h3>Email Verification Required</h3>
          <p>We've sent a verification email to <strong>{currentUser.email}</strong>.</p>
          <p>Please check your inbox and click the verification link to activate your account.</p>
          <button onClick={handleResendVerification} className="resend-button">
            Resend Verification Email
          </button>
        </div>
      ) : (
        <form onSubmit={handleLogin}>
          <input
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          <input
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
          {verificationError && <div className="error-message">{verificationError}</div>}
          <button type="submit">Login</button>
        </form>
      )}
      <div className="google-login">
        <button onClick={handleGoogleLogin} className="google-button">
          <FontAwesomeIcon icon={faGoogle} /> Login with Google
        </button>
      </div>
      <div className="register-link">
        <p>Don't have an account? <a href="/register">Register</a></p>
      </div>
    </div>
  );
};

export default Login;
